import type { ContentBlogUnifiedQuery$variables } from './__generated__/ContentBlogUnifiedQuery.graphql.ts';
import type { ContentUnifiedQuery$variables } from './__generated__/ContentUnifiedQuery.graphql.ts';
import type { ContentUnifiedWithSpaceKeyQuery$variables } from './__generated__/ContentUnifiedWithSpaceKeyQuery.graphql.ts';

const DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT = 4;

type SharedVariableOptions = {
	spaceKey?: string | null;
	versionOverride: number | null;
	isSSR: boolean;
	preloadLabels?: boolean;
	includeAgentTrustReceiptData?: boolean;
	includeBylineContributors?: boolean;
	includeMetadata?: boolean;
	includeReactions?: boolean;
	includeRestrictions?: boolean;
	includeRestrictionAncestors?: boolean;
	useContentModeFragment?: boolean;
};

type ContentVariableOptions = SharedVariableOptions & {
	contentId: string;
};

type PageContentVariableOptions = ContentVariableOptions & {
	agentSessionsUserId?: string | null;
};

type SpaceKeyVariableOptions = Omit<SharedVariableOptions, 'spaceKey'> & {
	spaceKey: string;
	agentSessionsUserId?: string | null;
};

const createSharedVariables = ({
	spaceKey,
	versionOverride,
	isSSR,
	preloadLabels = false,
	includeAgentTrustReceiptData = false,
	includeMetadata = false,
	includeReactions = false,
	includeRestrictions = false,
	includeRestrictionAncestors = false,
	useContentModeFragment = false,
}: SharedVariableOptions) => ({
	spaceKey,
	versionOverride,
	isSSR,
	useNewContentTopper: true,
	...(preloadLabels || includeMetadata ? { preloadLabels: true } : {}),
	...(includeAgentTrustReceiptData ? { includeAgentTrustReceiptData: true } : {}),
	...(includeMetadata ? { includeClassification: true, includeArchiveMetadata: true } : {}),
	...(includeReactions ? { includeReactions: true } : {}),
	...(includeRestrictions ? { includeRestrictions: true } : {}),
	...(includeRestrictionAncestors ? { includeRestrictionAncestors: true } : {}),
	...(useContentModeFragment ? { useContentModeFragment: true } : {}),
});

const createAgentSessionVariables = (agentSessionsUserId?: string | null) =>
	agentSessionsUserId
		? {
				agentSessionsPropertyKey: `cc-agent-mention-sessions:${agentSessionsUserId}`,
				fetchAgentSessionsMarker: true,
			}
		: {};

const createBylineVariables = (includeBylineContributors = false) =>
	includeBylineContributors
		? {
				includeBylineContributors: true,
				bylineContributorsLimit: DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT,
			}
		: {};

export const createContentUnifiedQueryVariables = ({
	contentId,
	agentSessionsUserId,
	includeBylineContributors,
	...sharedOptions
}: PageContentVariableOptions): ContentUnifiedQuery$variables => ({
	contentId,
	...createSharedVariables(sharedOptions),
	...createAgentSessionVariables(agentSessionsUserId),
	...createBylineVariables(includeBylineContributors),
});

export const createContentBlogUnifiedQueryVariables = ({
	contentId,
	includeBylineContributors,
	...sharedOptions
}: ContentVariableOptions): ContentBlogUnifiedQuery$variables => ({
	contentId,
	...createSharedVariables(sharedOptions),
	...createBylineVariables(includeBylineContributors),
});

export const createContentUnifiedWithSpaceKeyQueryVariables = ({
	spaceKey,
	agentSessionsUserId,
	includeBylineContributors,
	...sharedOptions
}: SpaceKeyVariableOptions): ContentUnifiedWithSpaceKeyQuery$variables => ({
	...createSharedVariables({ ...sharedOptions, spaceKey }),
	spaceKey,
	...createAgentSessionVariables(agentSessionsUserId),
	...(includeBylineContributors ? { includeBylineContributors: true } : {}),
});
